import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import TemplateEditor from "../components/TemplateEditor";
import api from "../auth/api";

export default function EnvioMasivoEmail() {

    const [workerRunning, setWorkerRunning] = useState(false);
    const workerTimer = useRef(null);


    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [fileName, setFileName] = useState("");

    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");

    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");

    // Nuevo: batch ID
    const [batchId, setBatchId] = useState(null);

    const [progress, setProgress] = useState({
        total: 0,
        sent: 0,
        pending: 0,
        failed: 0,
        status: "pending",
        inProgress: false,
    });

    const [detail, setDetail] = useState([]);
    const poll = useRef(null);

    const fileInput = useRef(null);

    const startWorker = (id = null) => {
        const theId = id ?? batchId;
        if (!theId) {
            return alert("No hay batch seleccionado para procesar.");
        }
        if (workerTimer.current) return; // evita iniciar dos veces
    
        setWorkerRunning(true);
        console.log(theId);
    
        workerTimer.current = setInterval(async () => {
            try {
                await api.post(`/email/worker/run/${theId}`);
                await loadBatchStatus(theId);
            } catch (e) {
                console.warn("Error en worker:", e);
            }
        }, 5000);
    };

    const loadBatchStatus = async (batchId) => {
        const res = await api.get(`/email/batch/${batchId}`);
        const data = res.data;

        const b = data.batch;

        setProgress({
            total: b.total,
            sent: b.sent,
            pending: b.pending,
            failed: b.failed,
            status: b.status,
            inProgress: b.status !== "finished",
        });

        setDetail(data.jobs);

        // stop automático si ya terminó
        if (b.pending === 0) {
            stopWorker();
        }
    };

    const stopWorker = () => {
        if (workerTimer.current) {
            clearInterval(workerTimer.current);
            workerTimer.current = null;
        }
        setWorkerRunning(false);
    };

    // ==================== EXCEL LOAD ====================
    const handleExcel = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = (evt) => {
            const data = new Uint8Array(evt.target.result);
            const wb = XLSX.read(data, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(ws, { header: 1 });
            const header = (json[0] || []).map((h) => String(h || "").trim());

            const body = json.slice(1).map((r) =>
                header.reduce((acc, col, i) => ({ ...acc, [col]: r[i] }), {})
            );

            setColumns(header);
            setRows(body);
        };
        reader.readAsArrayBuffer(file);
    };

    // ==================== START SEND ====================
    const startSend = async () => {
        if (!subject.trim() && !body.trim())
            return alert("Ingresa asunto o cuerpo del mensaje.");

        if (rows.length === 0)
            return alert("Debes cargar un archivo Excel.");

        try {
            const r = await api.post("/email/batch", {
                subject,
                body,
                rows,
                template_id: selectedTemplateId
            });

            setBatchId(r.data.batch_id);

            alert(`Lote creado (#${r.data.batch_id}) con ${r.data.total} correos.`);
            startPolling(r.data.batch_id);

        } catch (e) {
            console.error(e);
            alert("Error al iniciar lote.");
        }
    };

    // ==================== POLLING POR BATCH ====================
    const startPolling = (id) => {
        if (poll.current) clearInterval(poll.current);

        poll.current = setInterval(async () => {
            try {
                const r = await api.get(`/email/batch/${id}`);
                const b = r.data.batch;
                const j = r.data.jobs || [];

                setProgress({
                    total: b.total,
                    sent: b.sent,
                    pending: b.pending,
                    failed: b.failed,
                    status: b.status,
                    inProgress: b.status !== "finished",
                });

                setDetail(j);

                if (b.status === "finished") {
                    clearInterval(poll.current);
                    stopWorker(); // ⛔ detiene worker automáticamente

                    alert("El proceso ha terminado."); // 📢 mensaje al usuario
                    setBatchId(null);
                    setDetail([]);
                    setProgress((curr) => ({
                        ...curr,
                        inProgress: false, // ♻ vuelve a habilitar botón enviar
                    }));
                }

            } catch (e) {
                console.warn("Error leyendo progreso", e);
            }
        }, 2500);
    };

    // ==================== TEMPLATE SELECT ====================
    const handleSelectTemplate = (e) => {
        const id = e.target.value;
        setSelectedTemplateId(id);

        console.log("Selected template ID:", id);

        const tpl = templates.find(t => String(t.id) === String(id));

        if (tpl) {
            setSubject(tpl.email_subject || "");
            setBody(tpl.content_email || "");
        } else {
            setSubject("");
            setBody("");
        }
    };

    // ==================== LOAD TEMPLATES ====================
    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/templates/email");
                setTemplates(r.data?.templates || []);
            } catch (e) {
                console.warn("No se pudieron cargar plantillas");
            }
        })();
    }, []);

    useEffect(() => {
        return () => {
            if (workerTimer.current) clearInterval(workerTimer.current);
        };
    }, []);

    useEffect(() => {
        return () => stopWorker(); // se ejecuta cuando se desmonta el componente
    }, []);

    // Cleanup interval
    useEffect(() => () => poll.current && clearInterval(poll.current), []);

    return (
        <>
            <div className="page-title">Envío masivo de correos</div>

            {/* Upload Excel */}
            <section className="card section">
                <h3>1. Cargar Excel</h3>
                <input
                    ref={fileInput}
                    className="input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcel}
                />
                {rows.length > 0 && (
                    <div className="muted">
                        Archivo: <b>{fileName}</b> · Filas: <b>{rows.length}</b>
                    </div>
                )}
            </section>

            {/* Template */}
            <section className="card section">
                <h3>2. Plantilla de correo</h3>

                <label>
                    Seleccionar plantilla:
                    <select
                        className="input"
                        value={selectedTemplateId}
                        onChange={handleSelectTemplate}
                    >
                        <option value="">— Selecciona —</option>
                        {templates.map(t => (
                            <option key={t.id} value={t.id}>
                                {t.name}
                            </option>
                        ))}
                    </select>
                </label>

                <TemplateEditor
                    showEmail={true}
                    hideWhatsApp={true}
                    columns={columns}
                    emailSubject={subject}
                    emailBody={body}
                />
            </section>

            {/* Button send */}
            <section className="card section">
                <button
                    className="btn btn-primary"
                    onClick={startSend}
                    disabled={progress.inProgress}
                >
                    {progress.inProgress ? "Enviando..." : "Iniciar envío"}
                </button>
            </section>

            {/* Progress */}
            {batchId && (
                <section className="card section">
                    <h3>Progreso lote #{batchId}</h3>

                    <div>
                        Total: <b>{progress.total}</b> · Enviados: <b>{progress.sent}</b> ·
                        Pendientes: <b>{progress.pending}</b> · Errores:{" "}
                        <b>{progress.failed}</b>
                    </div>
                    {/* Botones de control */}
                    <div style={{ marginBottom: 10 }}>
                    {!workerRunning && (
                            <button className="btn btn-success" onClick={() => startWorker()}>
                                ▶ Iniciar procesamiento
                            </button>
                        )}
                        
                    </div>

                    <div style={{ background: "#222", height: 10, borderRadius: 4, marginTop: 6 }}>
                        <div
                            style={{
                                background: "#22c55e",
                                height: 10,
                                width:
                                    progress.total > 0
                                        ? `${Math.round((progress.sent / progress.total) * 100)}%`
                                        : "0%",
                                borderRadius: 4,
                                transition: "width .3s"
                            }}
                        />
                    </div>

                    {/* Detail table*/}
                    <table style={{ marginTop: 14 }}>
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Estado</th>
                                <th>Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detail.map((r) => (
                                <tr key={r.id}>
                                    <td>{r.email_to}</td>
                                    <td style={{
                                        color:
                                            r.status === "sent" ? "#22c55e"
                                                : r.status === "error" ? "#ef4444"
                                                    : "#e5e7eb"
                                    }}>
                                        {r.status}
                                    </td>
                                    <td>{r.error_msg || ""}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            )}
        </>
    );
}
